import { ref } from "vue";
import {
	getAllNotifications,
	markNotificationAsRead,
	markAllNotificationsAsRead,
	deleteNotification,
} from "~/services/notificationsService";

export function useUserNotifications() {
	const notifications = ref<Notification[]>([]);
	const error = ref<string | null>(null);
	const loading = ref<boolean>(false);
	const totalNotifications = ref<number>(0);
	const page = ref<number>(1);
	const notificationCount = ref(0);
	const getNotifications = async (p: number = 1) => {
		loading.value = true;
		try {
			const response = await getAllNotifications({
				"filter[mg_network_system_id]": import.meta.env.VITE_SYSTEM_ID,
				sort: "created_at",
				order: "desc",
				per_page: 6,
				page: p,
			});

			totalNotifications.value = response.data.meta.pagination.total;
			page.value = response.data.meta.pagination.current_page;
			const incoming = response.data.data.map((notification: any) => ({
				notification_id: notification.data.notification_id || notification.id || null,
				type: notification.mg_network_system_id,
				object: notification.data.object || null,
				id: notification.id || null,
				slug: notification.data.slug || null,
				url: notification.data.url || null,
				title: notification.data.title,
				message: notification.data.message,
				image: notification.data.image,
				read_at: notification.read_at,
				created_at: notification.created_at_diff,
			}));

			if (p === 1) {
				// Replace, never clear-then-append: the header and the sidebar both ask for page 1 as soon as the user is signed in,
				// and two appends after two clears showed every notification twice.
				notifications.value = incoming;
			} else {
				const known = new Set(notifications.value.map((n: any) => n.id ?? n.notification_id));
				notifications.value.push(...incoming.filter((n: any) => !known.has(n.id ?? n.notification_id)));
			}

			getNotificationsUnreadCount();
		} catch (err) {
			error.value = "Failed to fetch notifications";
		} finally {
			loading.value = false;
		}
	};

	const getNotificationsUnreadCount = async () => {
		let params = {
			"filter[mg_network_system_id]": import.meta.env.VITE_SYSTEM_ID,
			"filter[read]": 0,
			total: 1,
		};
		const response = await getAllNotifications(params);

		notificationCount.value = response.data.total;
	};

	const markAsRead = async (notificationId: any) => {
		try {
			await markNotificationAsRead(notificationId);
			notifications.value = notifications.value.map((notification: any) =>
				notification.id === notificationId
					? { ...notification, read_at: new Date().toISOString() }
					: notification,
			);
			getNotificationsUnreadCount();
		} catch (err) {
			error.value = "Failed to mark notification as read";
		}
	};

	const readAll = async () => {
		try {
			await markAllNotificationsAsRead();
			notificationCount.value = 0;
			notifications.value = notifications.value.map((notification) => ({
				...notification,
				read_at: new Date().toISOString(),
			}));
		} catch (err) {
			error.value = "Failed to mark all notifications as read";
		}
	};

	const deleteNotificationFunc = async (notificationId: any) => {
		try {
			await deleteNotification(notificationId);
			notifications.value = notifications.value.filter((notification: any) => notification.id !== notificationId);
			getNotificationsUnreadCount();
		} catch (err) {
			error.value = "Failed to delete notification";
		}
	};

	return {
		notifications,
		error,
		loading,
		getNotifications,
		markAsRead,
		readAll,
		totalNotifications,
		page,
		notificationCount,
		deleteNotification: deleteNotificationFunc,
	};
}
